/**
 * Swamithra — expert provisioning (US-01-005).
 *
 * SUPER ADMIN ONLY. Every route here returns 403 with no expert records in the
 * body for a session whose role is `expert`, and the attempt is written to the
 * audit log (US-01-005.20). The panel also hides the navigation entry, but the
 * hiding is cosmetic — the 403 is the control.
 *
 * THERE IS NO ROLE FIELD ANYWHERE IN THIS MODULE. The one super_admin is created
 * by a run-once deployment task from environment configuration, and the panel
 * exposes no screen or endpoint that creates or changes a role
 * (US-01-005.22).
 */

import { z } from "zod"

import {
	type CreateExpertInput,
	createExpertInputSchema,
	expertActivationSchema,
	expertSchema,
} from "../domain/expert"
import { api } from "./client"
import { type ReadOptions, type SubmitOptions } from "./options"

/**
 * The membership is NOT PAGED. It is the panel's roster — a handful of people —
 * and the service answers `{ "data": [...] }` with no pagination envelope at
 * all. Wrapping it in one would invent a total and a page count the response
 * never carried.
 */
const expertListSchema = z.object({ data: z.array(expertSchema) })

/**
 * Every Expert, active and inactive, with the fields the list renders:
 * name, number, status, created date and last login (US-01-005.16).
 *
 * `lastLoginAt` is null until the first successful verification and renders as
 * 'Never' — see `lastLoginLabel`.
 */
export function fetchExperts(options: ReadOptions = {}) {
	return api.get("/expert/experts", expertListSchema, options)
}

/**
 * Provisions an Expert.
 *
 * Created with `role=expert` and `isActive=true`, and able to request a login
 * OTP on the next attempt (US-01-005.4). A WhatsApp welcome message is sent
 * automatically with no per-Expert toggle; a delivery failure is retried and
 * logged WITHOUT blocking creation (US-01-005.14), so this call succeeding does
 * not mean the message arrived.
 *
 * A number that already belongs to an Expert in any format is rejected with
 * `duplicate_mobile_number` naming the existing Expert (US-01-005.15) — the
 * number is normalised to E.164 by `createExpertInputSchema` before it is sent,
 * so `09876543210` and `+91 98765 43210` collide as they should.
 */
export async function createExpert(input: CreateExpertInput, options: SubmitOptions) {
	return await api.post("/expert/experts", expertSchema, createExpertInputSchema.parse(input), {
		signal: options.signal,
		idempotencyKey: options.idempotencyKey,
	})
}

/**
 * Deactivates or reactivates an Expert.
 *
 * Deactivation takes effect on the Expert's VERY NEXT API call, because
 * `isActive` is re-checked on every authenticated request rather than only at
 * OTP issue (US-01-005.17, US-00-006.15). Reactivation needs no new provisioning
 * step (US-01-005.18).
 */
export async function setExpertActive(
	expertId: string,
	isActive: boolean,
	options: ReadOptions = {},
) {
	return await api.patch(
		`/expert/experts/${encodeURIComponent(expertId)}`,
		expertSchema,
		expertActivationSchema.parse({ isActive }),
		options,
	)
}
